import React from 'react'
import {ArrowLeft, AlertTriangle, Shield, CreditCard, FileText} from 'lucide-react'

interface TermsPageProps {
  onBack: () => void
}

export function TermsPage({ onBack }: TermsPageProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white mb-4">Terms of Service</h1>
          <p className="text-gray-400">Last updated: January 2025</p>
        </div>

        {/* Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <FileText className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Agreement to Terms</h2>
                <p className="text-gray-300 mb-3">
                  Welcome to Base Builders. By accessing or using our platform, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
                <p className="text-gray-300">
                  Base Builders is a platform that connects NFT and crypto project owners with potential investors and users through paid promotional listings on the Base blockchain ecosystem.
                </p>
              </div>
            </div>
          </section>

          {/* Subscription & Payment */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <CreditCard className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Subscription & Payment Terms</h2>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Project listings require a $20/month subscription paid through Stripe.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Subscriptions automatically renew monthly unless cancelled.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">You are solely responsible for managing and cancelling your Stripe subscription.</strong> To cancel, log into your Stripe account or contact Stripe support directly.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>No refunds will be provided for partial months or cancelled subscriptions.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>Upon successful payment, your project will be automatically listed on our platform.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>If payment fails or subscription expires, your project listing will be automatically removed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Content Guidelines & Moderation */}
          <section className="bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Content Guidelines & Prohibited Content</h2>
                <p className="text-gray-300 mb-4">
                  <strong className="text-white">Base Builders reserves the right to remove or ban any project profile deemed inappropriate, fraudulent, or in violation of these terms without prior notice or refund.</strong>
                </p>
                <p className="text-gray-300 mb-3">Prohibited content includes, but is not limited to:</p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Scam projects, rug pulls, or fraudulent schemes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Adult content, pornography, or sexually explicit material</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Hate speech, discrimination, or harassment</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Illegal activities or promotion of illegal goods/services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Impersonation of other projects or individuals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Malware, phishing, or malicious links</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400 mt-1">✗</span>
                    <span>Copyright infringement or unauthorized use of intellectual property</span>
                  </li>
                </ul>
                <p className="text-gray-300 mt-4">
                  <strong className="text-red-400">Important:</strong> If your profile is banned for violating these terms, <strong className="text-white">you remain responsible for cancelling your Stripe subscription</strong> to avoid future charges. Base Builders is not responsible for managing your Stripe billing.
                </p>
              </div>
            </div>
          </section>

          {/* User Responsibilities */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-[#60A5FA] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">Your Responsibilities</h2>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>You are responsible for all content uploaded to your project profile.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>You warrant that you own or have the rights to all images, videos, and content you upload.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>You must provide accurate and truthful information about your project.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span>You are responsible for maintaining the security of your account credentials.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#60A5FA] mt-1">•</span>
                    <span><strong className="text-white">You are solely responsible for managing and cancelling your Stripe subscription,</strong> including after account termination or profile removal.</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Limitation of Liability</h2>
            <p className="text-gray-300 mb-3">
              Base Builders provides a platform for project promotion and discovery. We do not:
            </p>
            <ul className="space-y-2 text-gray-300 mb-3">
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Endorse or verify the legitimacy of any listed projects</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Guarantee the accuracy of project information</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Take responsibility for investment decisions made by users</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#60A5FA] mt-1">•</span>
                <span>Manage or control Stripe subscriptions on behalf of users</span>
              </li>
            </ul>
            <p className="text-gray-300">
              Users are encouraged to conduct their own research (DYOR) before engaging with any project. Base Builders is not liable for any losses resulting from interactions with listed projects.
            </p>
          </section>

          {/* Changes to Terms */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Changes to Terms</h2>
            <p className="text-gray-300">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to the platform. Continued use of Base Builders after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-3">Contact Us</h2>
            <p className="text-gray-300 mb-2">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <a 
              href="https://t.me/chumponbase" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#60A5FA] hover:text-[#3B82F6] transition-colors"
            >
              Telegram: @chumponbase
            </a>
          </section>
        </div>
      </div>
    </div>
  )
}